import { Arr } from '@deqo/foundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ComponentType, Convention, ObjectManager, ObjectUpdate } from '../../../concepts/objects';
import { Line } from '../../../platform/geom/Line';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Modifiers } from '../../../platform/input/Modifiers';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { resolve } from '../../Container';
import { AutoLayoutEngine } from '../../packages/autoLayout/AutoLayoutEngine';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { GridSystem } from '../GridSystem';
import { PointerInfo } from '../PointerInfo';
import { RootOverride } from '../RootOverride';
import { Selection } from '../Selection';
import { swallow } from '../commands/OrderCommands';
import { IsCreating, Physical, RelativeTo } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { Root } from '../entities/Root';
import { HistoryManager } from '../history/HistoryManager';
import { EffectDirector, Effects } from '../visualization/EffectDirector';
import { ToolSettings } from './ToolBehavior';
import { toolEquip } from './ToolCommands';
import { ToolManager } from './ToolManager';
import { TransformToolBehavior } from './TransformToolBehavior';
import { SelectionScaleTransformation } from './transform/SelectionScaleTransformation';

export class CreateTransformableDragParams {

    public shouldFix?: boolean;
    public shouldSnap?: boolean;
    public shouldMirror?: boolean;
    public from: Point;
    public to: Point;

    constructor(from: Point) {
        this.from = this.to = from;
    }

    public distance() {
        return this.from.distanceTo(this.to);
    }

    public rect(grid: GridSystem) {

        const line = this.line(grid);
        return Rect.fromPoints([line.p1, line.p2]);
    }

    public line(grid: GridSystem) {
        let o = this.from;
        let t = this.to;

        if (this.shouldFix) t = this.fix(o, t);
        if (this.shouldSnap) t = grid.snap(t);
        if (this.shouldMirror) o = this.reflect(o, t);

        return new Line(o, t);
    }

    private fix(o: Point, t: Point) {
        const v = Point.vector(o, t);
        const q = v.quadrant();
        const a = axes[q];
        return v.project(a).add(o);
    }

    private reflect(o: Point, t: Point) {
        return o.add(Point.vector(o, t).inverse());
    }
}

const axes = {
    0: new Point(1, 1).normalize(),
    1: new Point(-1, 1).normalize(),
    2: new Point(-1, -1).normalize(),
    3: new Point(1, -1).normalize(),
};

type CreateMode = 'drag' | 'drop' | 'plop';

const mouseInputFromPointerInfo = (ptr: PointerInfo) =>
    new MouseInput(
        MouseButtons.Main,
        Modifiers.none,
        ptr.position,
        0,
        ptr.targets
    );

abstract class AbstractCreatePhysicalToolBehavior2<T extends Physical> extends TransformToolBehavior {

    @resolve(AutoLayoutEngine) protected autoLayout!: AutoLayoutEngine;
    @resolve(Camera) protected camera!: Camera;
    @resolve(CommandDispatcher) protected dispatch!: CommandDispatcher;
    @resolve(EffectDirector) protected effects!: EffectDirector;
    @resolve(GridSystem) protected grid!: GridSystem;
    @resolve(HistoryManager) protected history!: HistoryManager;
    @resolve(ObjectManager) protected objects!: ObjectManager;
    @resolve(EditorSession) protected session!: EditorSession;
    @resolve(ToolManager) protected tools!: ToolManager;

    protected settings: ToolSettings = {};

    private subject: T | null = null;
    private actualMode: CreateMode;

    protected constructor(
        private readonly type: ComponentType<T> | Convention<T>,
        private readonly defaultParam: string = 'none') {
        super();
    }

    protected get mode() {
        return this.actualMode;
    }

    public init(settings: ToolSettings, param: string) {
        super.init(settings, param);

        const [mode, edit] = (param || this.defaultParam).split(/:/g).map(x => x.trim()) as any;
        this.actualMode = mode || 'plop';

        // this.burden(
        //     this.overlay.add(gfx => {
        //         if (!this.subject?.alive || !this.subject.mounted()) return;
        //         this.paintSubject(this.subject, gfx);
        //     })
        // );
        this.settings = settings;
        // const [mode, edit] = (param || this.defaultParam).split(/:/g).map(x => x.trim()) as any;
        // this.actualMode = mode || 'plop';
        // this.edit = edit === 'edit';

        if (mode === 'drop' || mode === 'plop') {
            const mi = mouseInputFromPointerInfo(this.session.get(PointerInfo));
            this.initSubject(mi.position, true);
            this.prime(mi, { updateKey: this.updateKey() });
            this.begin(this.toInput(mi));
            return 'transforming';
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string {
        if (state !== 'idle') return;
        if (mi.button !== MouseButtons.Main) return;
        // assert(!this.subjects, 'AbstractMultiCreatePhysicalToolBehavior: creating with left over subjects?');

        if (this.mode === 'drag') {
            this.initSubject(mi.position, false);

            const sst = this.activator.call(SelectionScaleTransformation);
            sst.key = this.updateKey();

            const bounds = this.subject.bounds(RelativeTo.Model);

            sst.configure({
                origin: bounds.topLeft(),
                axis: new Point(1, 1).normalize(),
                handle: bounds.bottomRight(),
                // fixedMode: true,
            });

            this.prime(mi, { updateKey: this.updateKey(), override: sst });
            this.begin(this.toInput(mi));
            this.continue(this.toInput(mi));

            return 'transforming';
        }

        return super.onMouseDown(state, mi);
    }

    public onStateChanged(newState: string, oldState: string): void {
        if (oldState === 'transforming' && newState !== 'transforming') {
            this.finishSubject();
            this.dispatch(toolEquip, ['default', null, true]);
        }
    }

    protected createSubject(u: ObjectUpdate) {
        if (this.type instanceof Convention) {
            throw new Error('Convention mode requires full impl of createSubject()');
        }
        const subject = u.create(this.type);
        this.resolveRoot().add(subject);
        this.session.apply(Selection, x => x.replace([subject]));
        // Animate
        if (this.mode !== 'drag') {
            this.effects.trigger(Effects.popIn, subject);
        }
        return subject;
    }

    protected finalizeSubject(subject: T) {
        // noop
    }

    protected updateKey() {
        return `creating:${this.subject.id}`;
    }

    protected resolveRoot() {
        return this.session.get(RootOverride) ?? this.objects.get('root', Root);
    }

    protected shouldConsume() {
        return false;
    }

    private initSubject(vpt: Point, center: boolean) {

        this.objects.update(u => {
            // If not created yet, create
            const subject = this.subject ?? (this.subject = this.createSubject(u));
            u.rolling(this.updateKey());
            IsCreating.set(subject, true);

            // If we have a plop/drop point...
            if (vpt) {
                const mpt = this.camera.toModel(vpt);
                const spt = this.grid.shouldSnap(subject) ? this.grid.snap(mpt) : mpt;
                const rpt = this.resolveRoot()
                    .toRelative(spt)
                    .subtract(center ? subject.size().divide(2) : 0);
                subject.moveTo(rpt);
            }
        });
    }

    protected finishSubject() {
        if (!this.subject) return;
        this.objects.update(u => {
            u.rolling(this.updateKey());
            IsCreating.set(this.subject, false);
            this.finalizeSubject(this.subject);
            this.consumeIfRequired(this.subject);
        });
    }

    protected consumeIfRequired(subject: T) {
        if (this.shouldConsume()) {
            const placeInto = this.findDragPlaceInto(subject);
            const placeBefore = this.findDragPlaceBefore(placeInto, subject);
            if (placeBefore) {
                placeInto.addBefore(placeBefore, subject.removeSelf());
                subject.moveBy(placeInto.position(RelativeTo.Model).multiply(-1));

                this.dispatch(swallow, [subject.id]);
            }
        }
    }

    private findDragPlaceInto(subject: Physical) {
        const rect = subject.bounds(RelativeTo.Model);
        const hits = Physicals.bottomToTop(this.spatial.lookup(rect));
        for (const h of hits) {
            if (h === subject) continue;
            if (h.bounds(RelativeTo.Model).contains(rect)) {
                return h;
            }
        }
        return this.resolveRoot();
    }

    private findDragPlaceBefore(placeInto: Physical, subject: Physical) {
        const rect = subject.bounds(RelativeTo.Model);
        const hits = new Set(
            Array.from(this.spatial.lookup(rect))
                .filter(x => rect.contains(x.bounds(RelativeTo.Model)))
        );

        const filtered = Physicals.top(hits)
            .filter(x => x !== subject && x.parent === placeInto);

        return Arr.first(Physicals.topToBottom(filtered));
    }
}

// eslint-disable-next-line max-len
export const CreatePhysicalToolBehavior2 = <T extends Physical>(type: ComponentType<T> | Convention<T>, defaultParam?: string) =>
    class extends AbstractCreatePhysicalToolBehavior2<T> {
        constructor() {
            super(type, defaultParam);
        }
    };
